import { SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormControl, InputLabel, MenuItem, SelectChangeEvent } from '@mui/material';
import { CoreBox, CoreTypography } from '@youscience/core';
import { TaskStatus, TaskTags, Task } from '@common-types';
import { isEqual, isUndefined } from 'lodash-es';

import {
  ALL_TYPES_FILTER_VALUE,
  ARCHIVE_PAGE_TITLES,
  FILTER_TAGS_COLORS,
  FILTER_TAGS_DATA,
  INITIAL_FILTERS,
  SEASON_TABS_DATA,
  PLANNER_TIMELINE_TITLES,
  VALID_GRADES,
  SEASON_TITLE_BY_MODE,
  ALL_GRADES_VALUE,
} from '@constants/educationPlanner';
import { ROUTES } from '@constants/ROUTES';
import { BREADCRUMBS_PAGE_TEXT, BREADCRUMBS_NAVIGATION_DATA } from '@constants/breadcrumbs';
import { EDC_HASH } from '@constants';

import CustomTask from '@components/CustomTask';
import { EmptyStateText } from '@components/EmptyStateText';
import { GatherPersonalInfo } from '@components/GatherPersonalInfo';
import { Breadcrumbs } from '@components/Breadcrumbs';
import ActionButton from '@components/ActionButton';
import ProgressCircle from '@components/ProgressCircle';

import { useNeedMoreUserInfo } from '@hooks/useNeedMoreUserInfo';

import useAuthStore from '@store/AuthStore';

import useEducationPlannerStore from '@store/EducationPlannerStore';

import PlusIcon from '@images/plus-button.svg?react';
import NoResultsIcon from '@images/no-results.svg?react';

import { TaskCardSkeleton } from './components/TaskCard/TaskCardSkeleton';
import SchoolSelection from './components/SchoolSelectionPlanner';
import TaskCard from './components/TaskCard';
import Confetti from './components/Confetti';

import {
  sxStyles,
  MenuPropsStyles,
  StyledHeadSectionInner,
  StyledGradeSelect,
  StyledArchivePageTitle,
  StyledInfoSection,
  StyledSeasonTabs,
  StyledSeasonTabItem,
  StyledSeasonProgress,
  StyledActionsWrap,
  StyledFilterByStatusWrap,
  StyledFilterByStatusSelect,
  StyledFilterChipsWrap,
  StyledFilterItem,
  StyledProgressBar,
  StyledTaskCardsWrap,
} from './EducationPlanner.styles';

export const TASK_CARD_SKELETONS = Array.from({ length: 6 }, (el, index) => ({
  id: index,
  skeleton: TaskCardSkeleton,
}));

export const EducationPlanner = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isOpenCustomTask, setIsOpenCustomTask] = useState(false);
  const [isOpenSchoolSelection, setIsOpenSchoolSelection] = useState(false);
  const [isOpenPlannerQuestioner, setIsOpenPlannerQuestioner] = useState(false);

  const isNeedMoreUserInformation = useNeedMoreUserInfo();

  const setEducationPlannerData = useEducationPlannerStore((state) => state.setEducationPlannerData);
  const resetSchoolSelectionData = useEducationPlannerStore((state) => state.resetSchoolSelectionData);

  const userPlanning = useEducationPlannerStore((state) => state.userPlanning);

  const { currentGrade, currentSeason, isShowConfetti, pageTasks, pageFilters, tagColor, isTaskArchivePage } =
    useEducationPlannerStore((state) => state.educationPlannerData);

  const { completedTaskPercentage } = useEducationPlannerStore(
    (state) => {
      const { userPlanning, educationPlannerData } = state;
      const { currentGrade, currentSeason, isTaskArchivePage } = educationPlannerData;

      if (!userPlanning?.tasks) {
        return { completedTaskCount: 0, completedTaskPercentage: 0 };
      }

      let filteredTasks: Task[];

      if (isTaskArchivePage) {
        filteredTasks = userPlanning.tasks.filter((task) => task.isDeleted) || [];
      } else {
        filteredTasks = VALID_GRADES.includes(currentGrade)
          ? userPlanning.tasks.filter(
              (task) => task.grades.includes(currentGrade) && task.season === currentSeason && !task.isDeleted,
            )
          : userPlanning.tasks.filter((task) => task.season === currentSeason && !task.isDeleted);
      }

      const completed = filteredTasks.filter((task) => task.status === TaskStatus.COMPLETED).length;
      const percentage = Math.round((completed * 100) / (filteredTasks.length || 1)) || 0;

      return {
        completedTaskCount: completed,
        completedTaskPercentage: percentage,
      };
    },

    (prev, next) => {
      const prevCount = prev.completedTaskCount;
      const prevPercentage = prev.completedTaskPercentage;
      const nextCount = next.completedTaskCount;
      const nextPercentage = next.completedTaskPercentage;

      const isEqual = prevCount === nextCount && prevPercentage === nextPercentage;

      return isEqual;
    },
  );

  const { userData } = useAuthStore((state) => state.authSession);

  const displayName = userData?.displayName || userData?.fullName.split(' ')[0] || '';

  const tasksBySelectedGrade =
    userPlanning?.tasks?.filter((task) => {
      if (VALID_GRADES.includes(currentGrade)) {
        return task.grades.includes(currentGrade) && !task.isDeleted;
      } else {
        return !task.isDeleted;
      }
    }) || [];

  const completedTasksByGrade = tasksBySelectedGrade.filter((task) => task.status === TaskStatus.COMPLETED).length;
  const taskPercentageByGrade = Math.round((completedTasksByGrade * 100) / (tasksBySelectedGrade.length || 1)) || 0;

  const pageTitle = (
    <CoreBox component="span">
      Hi
      {displayName ? (
        <CoreBox component="span" sx={sxStyles.capitalize}>
          &nbsp;{displayName}
        </CoreBox>
      ) : (
        ''
      )}
      ! Meet your college planning timeline.
    </CoreBox>
  );

  const emptyListTitle = isEqual(INITIAL_FILTERS, pageFilters) || pageTasks.length === 0 ? 'No tasks' : 'No results';
  const emptyListSubtitle =
    currentSeason === null ? ARCHIVE_PAGE_TITLES.emptyListSubtitle : PLANNER_TIMELINE_TITLES.emptyListSubtitle;

  const handleCancelQuestioner = () => {
    setIsOpenPlannerQuestioner(false);

    navigate(ROUTES.ROOT);
  };

  const handleSaveQuestioner = () => {
    setIsOpenPlannerQuestioner(false);
  };

  const handleChangeGrade = (event: SelectChangeEvent<unknown>) => {
    setEducationPlannerData({ currentGrade: event.target.value as number });
  };

  const handleChangeFilterByStatus = (event: SelectChangeEvent<unknown>) => {
    let updatedFilters = { ...pageFilters };

    if (!event.target.value) {
      updatedFilters = { ...pageFilters, status: INITIAL_FILTERS.status };
    } else {
      updatedFilters = { ...pageFilters, status: event.target.value as TaskStatus };
    }

    setEducationPlannerData({ pageFilters: updatedFilters });
  };

  const handleFilterByTag = (tag: string) => () => {
    if (pageFilters.tag === tag) return;

    let updatedFilters = { ...pageFilters };

    if (tag === ALL_TYPES_FILTER_VALUE) {
      updatedFilters = { ...pageFilters, tag: INITIAL_FILTERS.tag };
    } else {
      updatedFilters = { ...pageFilters, tag };
    }

    setEducationPlannerData({ pageFilters: updatedFilters, tagColor: FILTER_TAGS_COLORS[tag] });
  };

  useEffect(() => {
    if (isNeedMoreUserInformation) {
      setIsOpenPlannerQuestioner(true);
    }
  }, [isNeedMoreUserInformation]);

  useEffect(() => {
    if (!userPlanning?.tasks) {
      return;
    }

    if (isTaskArchivePage) {
      const archivedTasks = userPlanning.tasks.filter((task) => task.isDeleted) || [];
      setEducationPlannerData({ pageTasks: archivedTasks });
      return;
    }

    const seasonTasks = VALID_GRADES.includes(currentGrade)
      ? userPlanning.tasks.filter(
          (task) => task.grades.includes(currentGrade) && task.season === currentSeason && !task.isDeleted,
        )
      : userPlanning.tasks.filter((task) => task.season === currentSeason && !task.isDeleted);

    setEducationPlannerData({ pageTasks: seasonTasks || [] });
  }, [currentGrade, currentSeason, userPlanning?.tasks]);

  const handleAddCustomTask = () => {
    setIsOpenCustomTask(true);
  };

  const handleCloseCustomTask = () => {
    setIsOpenCustomTask(false);
  };

  const handleCloseSchoolSelection = () => {
    resetSchoolSelectionData();
    setIsOpenSchoolSelection(false);
  };

  const handleCloseConfetti = () => {
    setEducationPlannerData({ isShowConfetti: false });
  };

  const handleCustomTaskEdit = (task: Task) => {
    setEducationPlannerData({ currentTask: task });
    setIsOpenCustomTask(true);
  };

  const handleSeasonChange = (event: SyntheticEvent, seasonMode: number) => {
    setEducationPlannerData({ currentSeason: SEASON_TITLE_BY_MODE[seasonMode] });
  };

  const filteredTasks = useMemo(() => {
    if (!pageTasks?.length) return [];

    return pageTasks.reduce((acc: Task[], task: Task) => {
      const isStatusFilterAll = pageFilters.status === ALL_TYPES_FILTER_VALUE;
      const isTagFilterAll = pageFilters.tag === ALL_TYPES_FILTER_VALUE;

      const isStatusEqual = task.status === pageFilters.status;
      const isTagSelected = task.tags?.includes(pageFilters.tag as TaskTags);

      if (!isStatusFilterAll && !isTagFilterAll && isStatusEqual && isTagSelected) {
        return [...acc, task];
      }

      if (!isStatusFilterAll && isTagFilterAll && isStatusEqual) {
        return [...acc, task];
      }

      if (isStatusFilterAll && !isTagFilterAll && isTagSelected) {
        return [...acc, task];
      }

      return acc;
    }, [] as Task[]);
  }, [pageFilters, pageTasks]);

  const renderTaskArr = isEqual(INITIAL_FILTERS, pageFilters) ? pageTasks || [] : filteredTasks || [];

  return (
    <>
      {!isTaskArchivePage && (
        <CoreBox sx={sxStyles.headSectionWrap}>
          <StyledHeadSectionInner>
            <Breadcrumbs
              links={BREADCRUMBS_NAVIGATION_DATA[location.hash] || BREADCRUMBS_NAVIGATION_DATA[EDC_HASH]}
              currentPage={BREADCRUMBS_PAGE_TEXT.EDUCATION_PLANNER}
            />

            <CoreBox sx={sxStyles.headSectionData}>
              <CoreTypography data-testid="pageTitle" variant="h4" component="h3" sx={sxStyles.headTitle}>
                {pageTitle}
              </CoreTypography>

              <FormControl sx={sxStyles.gradeWrap}>
                <InputLabel>Grade</InputLabel>

                <StyledGradeSelect
                  data-testid="gradeSelect"
                  MenuProps={MenuPropsStyles}
                  value={VALID_GRADES.includes(currentGrade) ? currentGrade : ALL_GRADES_VALUE}
                  label="Grade"
                  onChange={handleChangeGrade}
                >
                  <MenuItem value={9}>9</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={11}>11</MenuItem>
                  <MenuItem value={12}>12</MenuItem>
                  <MenuItem value={0}>All grades</MenuItem>
                </StyledGradeSelect>
              </FormControl>
            </CoreBox>

            <CoreBox sx={sxStyles.headSectionStatistics}>
              <CoreBox sx={sxStyles.completedTasks}>
                {completedTasksByGrade === tasksBySelectedGrade.length && (
                  <CoreTypography data-testid="congratulationsMessage" variant="caption">
                    🎉 Congratulations! You completed all tasks.
                  </CoreTypography>
                )}

                <CoreTypography data-testid="completedTaskCounter" variant="caption">
                  {`Completed tasks: ${completedTasksByGrade}/${tasksBySelectedGrade.length} (${taskPercentageByGrade}%)`}
                </CoreTypography>
              </CoreBox>
            </CoreBox>
          </StyledHeadSectionInner>

          <CoreBox data-testid="progressBar" sx={sxStyles.progressBarWrap}>
            <StyledProgressBar completed={taskPercentageByGrade} />
          </CoreBox>
        </CoreBox>
      )}

      {isTaskArchivePage ? (
        <StyledArchivePageTitle>
          <CoreTypography data-testid="archive-page-title" variant="h4" component="h3">
            {ARCHIVE_PAGE_TITLES.title}
          </CoreTypography>

          <CoreTypography data-testid="pageSubtitle" sx={sxStyles.pageSubtitle}>
            {ARCHIVE_PAGE_TITLES.subtitle}
          </CoreTypography>
        </StyledArchivePageTitle>
      ) : null}

      <StyledInfoSection>
        {currentSeason ? (
          <StyledSeasonTabs value={SEASON_TABS_DATA[currentSeason].mode} onChange={handleSeasonChange}>
            {Object.values(SEASON_TABS_DATA).map(({ mode, icon, title }) => {
              return <StyledSeasonTabItem key={mode} icon={icon} label={title} />;
            })}
          </StyledSeasonTabs>
        ) : null}

        {!isTaskArchivePage ? (
          <StyledSeasonProgress>
            <ProgressCircle filling={completedTaskPercentage} size={56} />

            <CoreTypography data-testid="pageSubtitle" sx={sxStyles.pageSubtitle}>
              {PLANNER_TIMELINE_TITLES.subtitle}
            </CoreTypography>
          </StyledSeasonProgress>
        ) : null}

        <StyledActionsWrap>
          {!isTaskArchivePage && (
            <StyledFilterByStatusWrap>
              <CoreTypography>Status:</CoreTypography>

              <StyledFilterByStatusSelect
                data-testid="filterByStatus"
                value={pageFilters.status}
                onChange={handleChangeFilterByStatus}
              >
                <MenuItem value={ALL_TYPES_FILTER_VALUE}>All</MenuItem>

                <MenuItem value={TaskStatus.ACTIVE}>To do</MenuItem>

                <MenuItem value={TaskStatus.COMPLETED}>Completed</MenuItem>
              </StyledFilterByStatusSelect>
            </StyledFilterByStatusWrap>
          )}

          <StyledFilterChipsWrap color={tagColor}>
            {FILTER_TAGS_DATA.map((item) => {
              const isSelected = item.value === pageFilters.tag;

              return (
                <StyledFilterItem
                  data-testid="filterByTag"
                  key={item.color}
                  value={item.value}
                  selected={isSelected}
                  color={item.color}
                  onClick={handleFilterByTag(item.value)}
                >
                  {item.value}
                </StyledFilterItem>
              );
            })}
          </StyledFilterChipsWrap>

          {!isTaskArchivePage && (
            <ActionButton data-testid="addCustomTask" onClick={handleAddCustomTask} startIcon={<PlusIcon />}>
              Add task
            </ActionButton>
          )}
        </StyledActionsWrap>
      </StyledInfoSection>

      <StyledTaskCardsWrap>
        {renderTaskArr.map((task) => (
          <TaskCard key={task._id?.toString()} task={task} handleEditTask={handleCustomTaskEdit} />
        ))}

        {isUndefined(userPlanning.tasks) &&
          TASK_CARD_SKELETONS.map((skeletonCard) => {
            return <TaskCardSkeleton key={skeletonCard.id} />;
          })}

        {!isUndefined(userPlanning.tasks) && renderTaskArr.length === 0 && (
          <EmptyStateText icon={<NoResultsIcon />} title={emptyListTitle} subtitle={emptyListSubtitle} />
        )}
      </StyledTaskCardsWrap>

      {isOpenCustomTask && <CustomTask isOpenTask={isOpenCustomTask} onCloseTask={handleCloseCustomTask} />}

      {isOpenSchoolSelection && (
        <SchoolSelection
          isOpenSchoolSelection={isOpenSchoolSelection}
          onCloseSchoolSelection={handleCloseSchoolSelection}
        />
      )}

      {isShowConfetti && (
        <Confetti
          isShowConfetti={isShowConfetti}
          handleCloseConfetti={handleCloseConfetti}
          confettiStatus={completedTasksByGrade === tasksBySelectedGrade.length ? 'grade' : 'season'}
        />
      )}

      {isOpenPlannerQuestioner && (
        <GatherPersonalInfo
          isOpenGatherPersonalInfo={isOpenPlannerQuestioner}
          onSave={handleSaveQuestioner}
          onCancel={handleCancelQuestioner}
          summaryTitle="We are loading your personal timeline..."
          saveTitle="Confirm"
          cancelTitle="Cancel"
        />
      )}
    </>
  );
};
